import { useState } from 'react';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, TableWrap, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { FieldChips, FIELDS, reasonText, useInsert } from './OutreachMail.tsx';
import { Table } from './Reports.tsx';

/**
 * Follow-up sequences: a few e-mails to one person, days apart, that stop by themselves when the
 * person answers, asks for no more, bounces or their company moves on.
 */

type SeqState = 'draft' | 'active' | 'paused';
type EnrState = 'active' | 'finished' | 'stopped';
interface SequenceRow {
  id: string;
  name: string;
  description: string | null;
  state: SeqState;
  active: boolean;
  steps: number;
  days: number;
  people: number;
  active_people: number;
  finished: number;
  answered: number;
}
interface Step {
  wait_days: number;
  subject: string | null;
  body: string;
  same_thread: boolean;
}
interface SequenceFull {
  id: string;
  name: string;
  description: string | null;
  state: SeqState;
  active: boolean;
  steps: (Step & { id: string; position: number })[];
  people: {
    id: string;
    contact: string;
    company: string;
    state: EnrState;
    step: number;
    next_at: string | null;
    stop_reason: string | null;
    started_at: string;
    finished_at: string | null;
    first_name: string;
    last_name: string | null;
    email: string | null;
    company_name: string;
  }[];
}
export interface CompanySequence {
  id: string;
  contact: string;
  sequence: string;
  sequence_name: string;
  state: EnrState;
  step: number;
  steps: number;
  next_at: string | null;
  stop_reason: string | null;
}

const SEQ_LOOK: Record<SeqState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  active: { tone: 'ok', icon: 'play' },
  paused: { tone: 'warn', icon: 'pause' },
};
const ENR_LOOK: Record<EnrState, { tone: Tone; icon: IconName }> = {
  active: { tone: 'info', icon: 'repeat' },
  finished: { tone: 'neutral', icon: 'checkCircle' },
  stopped: { tone: 'neutral', icon: 'x' },
};

export function SeqBadge({ state }: { state: SeqState }) {
  const look = SEQ_LOOK[state];
  return <StatusBadge tone={look.tone} icon={look.icon} label={t(`sq.st_${state}` as MessageKey)} />;
}

/** Where a person is in a sequence, in words. */
export function enrollmentText(e: { state: EnrState; step: number; steps?: number; next_at: string | null; stop_reason: string | null }) {
  if (e.state === 'finished') return t('sq.e_finished');
  if (e.state === 'stopped') return t('sq.e_stopped', { why: t(`sq.why_${e.stop_reason ?? 'manual'}` as MessageKey) });
  return e.next_at ? t('sq.e_next', { n: e.step + 1, when: dateTime(e.next_at) }) : t('sq.e_waiting');
}

const personName = (p: { first_name: string; last_name: string | null }) => [p.first_name, p.last_name].filter(Boolean).join(' ');

// ---- the list ----------------------------------------------------------------------------------

export function SequencesTab() {
  const { can } = useSession();
  const manage = can('outreach.manage');
  const [archived, setArchived] = useState(false);
  const { data, reload } = useAction<SequenceRow[]>('outreach.sequences', { archived });
  const [editing, setEditing] = useState<SequenceFull | 'new' | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section className="out-section" aria-label={t('out.tab_sequences')}>
      <div className="out-filters om-filters">
        <p className="muted small">{t('sq.intro')}</p>
        <span className="row-actions">
          <label className="out-toggle">
            <Switch checked={archived} onChange={setArchived} label={t('om.show_archived')} />
            <span>{t('om.show_archived')}</span>
          </label>
          {manage ? (
            <Button icon="plus" variant="primary" onClick={() => setEditing('new')}>
              {t('sq.new')}
            </Button>
          ) : null}
        </span>
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : !data.length ? (
        <EmptyState
          icon="repeat"
          title={archived ? t('sq.none_archived') : t('sq.none')}
          body={archived ? undefined : t('sq.none_hint')}
          action={
            manage && !archived ? (
              <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
                {t('sq.new')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="om-templates">
          {data.map((q) => (
            <li key={q.id} className="panel sq-card">
              <button type="button" className="sq-open" onClick={() => setOpen(q.id)} aria-label={t('sq.open', { name: q.name })}>
                <span className="panel-head">
                  <span className="om-sub">{q.name}</span>
                  <SeqBadge state={q.state} />
                </span>
                {q.description ? <span className="muted small">{q.description}</span> : null}
                <span className="sq-facts">
                  <span>
                    <Icon name="mail" size={14} /> {tn('sq.n_emails', q.steps)}
                  </span>
                  <span>
                    <Icon name="calendar" size={14} /> {tn('sq.n_days', q.days)}
                  </span>
                  <span>
                    <Icon name="users" size={14} /> {tn('sq.n_in', q.active_people)}
                  </span>
                  <span>
                    <Icon name="reply" size={14} /> {t('sq.answered', { n: q.answered, of: q.people })}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open ? (
        <SequenceDialog
          id={open}
          onClose={() => setOpen(null)}
          onEdit={(q) => (setOpen(null), setEditing(q))}
          onChanged={async () => {
            await reload();
          }}
        />
      ) : null}
      {editing ? (
        <SequenceForm
          sequence={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async (id) => {
            setEditing(null);
            await reload();
            setOpen(id);
          }}
        />
      ) : null}
    </section>
  );
}

// ---- one sequence ------------------------------------------------------------------------------

function SequenceDialog({ id, onClose, onEdit, onChanged }: { id: string; onClose: () => void; onEdit: (q: SequenceFull) => void; onChanged: () => Promise<void> }) {
  const { can } = useSession();
  const manage = can('outreach.manage');
  const send = can('outreach.send');
  const { data: q, reload } = useAction<SequenceFull>('outreach.sequence', { id });
  const { busy, run } = useSave();
  if (!q) {
    return (
      <Dialog title={t('common.loading')} onClose={onClose} size="lg">
        <Skeleton h={320} />
      </Dialog>
    );
  }
  const changed = async () => {
    await reload();
    await onChanged();
  };
  const setState = (state: 'active' | 'paused') =>
    void run(
      async () => {
        await action('outreach.sequence_state', { id: q.id, state });
        await changed();
      },
      state === 'active' ? t('sq.started') : t('sq.paused'),
    );
  const inIt = q.people.filter((p) => p.state === 'active').length;
  let day = 0;
  return (
    <Dialog
      title={q.name}
      onClose={onClose}
      size="lg"
      footer={
        manage ? (
          <>
            <span className="foot-actions">
              <Button
                variant="ghost"
                icon={q.active ? 'archive' : 'undo'}
                busy={busy}
                onClick={() =>
                  void run(
                    async () => {
                      await action('outreach.archive_sequence', { id: q.id, active: !q.active });
                      await changed();
                    },
                    q.active ? t('sq.archived') : t('sq.restored'),
                  )
                }
              >
                {q.active ? t('sq.archive') : t('sq.restore')}
              </Button>
              {q.active ? (
                <Button variant="ghost" icon="edit" onClick={() => onEdit(q)}>
                  {t('sq.edit')}
                </Button>
              ) : null}
            </span>
            {q.active && q.state === 'active' ? (
              <Button variant="secondary" icon="pause" busy={busy} onClick={() => setState('paused')}>
                {t('sq.pause')}
              </Button>
            ) : q.active ? (
              <Button variant="primary" icon="play" busy={busy} onClick={() => setState('active')}>
                {q.state === 'paused' ? t('sq.resume') : t('sq.start')}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      <div className="form-stack">
        <div className="out-head">
          <SeqBadge state={q.state} />
          <span className="muted small">{q.state === 'draft' ? t('sq.draft_note') : q.state === 'paused' ? t('sq.paused_note') : t('sq.active_note')}</span>
        </div>
        {q.description ? <p className="out-note">{q.description}</p> : null}
        <section className="panel" aria-labelledby="sq-steps">
          <div className="panel-head">
            <h2 id="sq-steps">{t('sq.steps')}</h2>
          </div>
          <ol className="sq-steps">
            {q.steps.map((s, i) => {
              day += s.wait_days;
              return (
                <li key={s.id}>
                  <span className="sq-day">{i === 0 ? t('sq.day_first') : t('sq.day_n', { n: day + 1 })}</span>
                  <div>
                    <span className="strong">{s.subject ?? (s.same_thread ? t('sq.same_thread_subject') : '—')}</span>
                    <span className="muted small om-snippet">{s.body}</span>
                  </div>
                </li>
              );
            })}
          </ol>
          <p className="muted small">
            <Icon name="shieldCheck" size={14} /> {t('sq.stops_when')}
          </p>
        </section>
        <section className="panel" aria-labelledby="sq-people">
          <div className="panel-head">
            <h2 id="sq-people">{tn('sq.people_n', q.people.length)}</h2>
            <span className="muted small">{tn('sq.n_in', inIt)}</span>
          </div>
          {!q.people.length ? (
            <EmptyState icon="users" title={t('sq.no_people')} body={t('sq.no_people_hint')} />
          ) : (
            <TableWrap label={t('sq.people')}>
              <Table
                view="outreach.enrollments"
                head={[t('sq.c_person'), t('sq.c_where'), t('sq.c_started'), '']}
                phoneHide={[2]}
                left={[0, 1]}
                empty={t('sq.no_people')}
                rows={q.people.map((p) => ({
                  key: p.id,
                  sort: [personName(p).toLowerCase(), p.state, p.started_at, null],
                  cells: [
                    <span key="p" className="vat-party">
                      <span className="strong">{personName(p)}</span>
                      <span className="muted small">{p.company_name}</span>
                    </span>,
                    <span key="w" className="vat-party">
                      <StatusBadge tone={ENR_LOOK[p.state].tone} icon={ENR_LOOK[p.state].icon} label={t(`sq.es_${p.state}` as MessageKey)} />
                      <span className="muted small">{enrollmentText({ ...p, steps: q.steps.length })}</span>
                    </span>,
                    <span key="s" className="muted small">
                      {since(p.started_at)}
                    </span>,
                    send && p.state === 'active' ? (
                      <IconButton
                        key="x"
                        icon="x"
                        label={t('sq.stop_person', { name: personName(p) })}
                        disabled={busy}
                        onClick={() =>
                          void run(async () => {
                            await action('outreach.stop_enrollment', { id: p.id });
                            await changed();
                          }, t('sq.person_stopped'))
                        }
                      />
                    ) : (
                      <span key="x" />
                    ),
                  ],
                }))}
              />
            </TableWrap>
          )}
        </section>
      </div>
    </Dialog>
  );
}

// ---- writing a sequence ------------------------------------------------------------------------

const blankStep = (first: boolean): Step => ({ wait_days: first ? 0 : 3, subject: first ? '' : null, body: '', same_thread: !first });

function StepEditor({ step, index, count, tried, onChange, onMove, onRemove }: { step: Step; index: number; count: number; tried: boolean; onChange: (s: Step) => void; onMove: (by: -1 | 1) => void; onRemove: () => void }) {
  const [focus, setFocus] = useState<'subject' | 'body'>('body');
  const subj = useInsert(step.subject ?? '', (v) => onChange({ ...step, subject: v }));
  const text = useInsert(step.body, (v) => onChange({ ...step, body: v }));
  const first = index === 0;
  const needsSubject = first || !step.same_thread;
  return (
    <li className="sq-edit-step">
      <div className="panel-head">
        <h3 className="om-sub">{t('sq.email_n', { n: index + 1 })}</h3>
        <span className="row-actions">
          <IconButton icon="up" label={t('sq.move_up', { n: index + 1 })} disabled={index === 0} onClick={() => onMove(-1)} />
          <IconButton icon="down" label={t('sq.move_down', { n: index + 1 })} disabled={index === count - 1} onClick={() => onMove(1)} />
          <IconButton icon="trash" label={t('sq.remove', { n: index + 1 })} disabled={count === 1} onClick={onRemove} />
        </span>
      </div>
      {first ? (
        <p className="muted small">{t('sq.first_goes')}</p>
      ) : (
        <div className="form-grid">
          <Field label={t('sq.wait')} hint={t('sq.wait_hint')}>
            <input className="input" type="number" inputMode="numeric" min={0} max={90} value={step.wait_days} onChange={(e) => onChange({ ...step, wait_days: Math.max(0, Math.min(90, Number(e.target.value) || 0)) })} />
          </Field>
          <label className="out-toggle sq-thread">
            <Switch checked={step.same_thread} onChange={(v) => onChange({ ...step, same_thread: v })} label={t('sq.same_thread')} />
            <span>
              {t('sq.same_thread')}
              <span className="muted small block">{t('sq.same_thread_hint')}</span>
            </span>
          </label>
        </div>
      )}
      <Field
        label={needsSubject ? t('om.subject') : t('sq.subject_optional')}
        required={needsSubject}
        error={tried && needsSubject && !step.subject?.trim() ? t('om.e_subject') : null}
        hint={needsSubject ? undefined : t('sq.subject_optional_hint')}
      >
        <input ref={subj.ref} className="input" value={step.subject ?? ''} maxLength={200} onFocus={() => setFocus('subject')} onChange={(e) => onChange({ ...step, subject: e.target.value })} />
      </Field>
      <Field label={t('om.body')} required error={tried && !step.body.trim() ? t('om.e_body') : null}>
        <textarea ref={text.ref} className="input om-body" rows={first ? 8 : 5} value={step.body} maxLength={20000} onFocus={() => setFocus('body')} onChange={(e) => onChange({ ...step, body: e.target.value })} />
      </Field>
      <FieldChips onPick={(f) => (focus === 'subject' ? subj.insert(f) : text.insert(f))} />
    </li>
  );
}

function SequenceForm({ sequence, onClose, onSaved }: { sequence: SequenceFull | null; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const [name, setName] = useState(sequence?.name ?? '');
  const [description, setDescription] = useState(sequence?.description ?? '');
  const [list, setList] = useState<Step[]>(sequence?.steps.map(({ wait_days, subject, body, same_thread }) => ({ wait_days, subject, body, same_thread })) ?? [blankStep(true), blankStep(false)]);
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const unknown = [...new Set(list.flatMap((s) => [...`${s.subject ?? ''}\n${s.body}`.matchAll(/\{\s*([a-zA-Z_]+)\s*(?:\|[^{}]*)?\}/g)].map((m) => m[1]!).filter((f) => !(FIELDS as readonly string[]).includes(f.toLowerCase()))))];
  const bad = !name.trim() || list.some((s, i) => !s.body.trim() || ((i === 0 || !s.same_thread) && !s.subject?.trim())) || unknown.length > 0;
  const total = list.reduce((n, s, i) => n + (i === 0 ? 0 : s.wait_days), 0);
  const move = (i: number, by: -1 | 1) =>
    setList((l) => {
      const out = [...l];
      [out[i], out[i + by]] = [out[i + by]!, out[i]!];
      return out;
    });
  const save = () => {
    setTried(true);
    if (bad) return;
    void run(
      async () => {
        const q = await action<{ id: string }>('outreach.save_sequence', {
          id: sequence?.id,
          name: name.trim(),
          description: description.trim() || null,
          steps: list.map((s) => ({ ...s, subject: s.subject?.trim() || null, body: s.body.trim() })),
        });
        await onSaved(q.id);
      },
      sequence ? t('sq.saved') : t('sq.added'),
    );
  };
  return (
    <Dialog
      title={sequence ? t('sq.edit_named', { name: sequence.name }) : t('sq.new')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="foot-actions muted small">{t('sq.total', { emails: tn('sq.n_emails', list.length), days: tn('sq.n_days', total) })}</span>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {sequence ? t('common.save') : t('sq.add')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('sq.name')} required error={tried && !name.trim() ? t('sq.e_name') : null} hint={t('sq.name_hint')}>
          <input className="input" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('sq.description')}>
          <input className="input" value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        {sequence && sequence.people.some((p) => p.state === 'active') ? (
          <p className="callout callout-info">
            <Icon name="info" size={16} />
            <span>{t('sq.edit_live')}</span>
          </p>
        ) : null}
        <ol className="sq-edit-steps">
          {list.map((s, i) => (
            <StepEditor
              key={i}
              step={s}
              index={i}
              count={list.length}
              tried={tried}
              onChange={(next) => setList((l) => l.map((x, j) => (j === i ? next : x)))}
              onMove={(by) => move(i, by)}
              onRemove={() => setList((l) => l.filter((_, j) => j !== i))}
            />
          ))}
        </ol>
        {unknown.length ? (
          <p className="callout callout-danger" role="alert">
            <Icon name="alert" size={16} />
            <span>{t('om.unknown_fields', { fields: unknown.map((f) => `{${f}}`).join(', ') })}</span>
          </p>
        ) : null}
        {list.length < 8 ? (
          <Button variant="secondary" icon="plus" onClick={() => setList((l) => [...l, blankStep(false)])}>
            {t('sq.add_step')}
          </Button>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- adding people -----------------------------------------------------------------------------

export function EnrollDialog({ contacts = [], companies = [], onClose, onDone }: { contacts?: string[]; companies?: string[]; onClose: () => void; onDone: () => Promise<void> }) {
  const { data: list } = useAction<SequenceRow[]>('outreach.sequences', { archived: false });
  const [chosen, setChosen] = useState('');
  const [result, setResult] = useState<{ enrolled: number; skipped: { contact: string; name: string; reason: string; fields?: string[] }[]; state: SeqState } | null>(null);
  const { busy, run } = useSave();
  const pick = chosen || list?.find((q) => q.state === 'active')?.id || list?.[0]?.id || '';
  const q = list?.find((x) => x.id === pick);
  const go = () =>
    void run(async () => {
      setResult(await action('outreach.enroll', { sequence: pick, contacts, companies }));
      await onDone();
    });
  if (result) {
    return (
      <Dialog
        title={t('sq.added_title')}
        onClose={onClose}
        size="md"
        footer={
          <Button variant="primary" icon="check" onClick={onClose}>
            {t('om.done')}
          </Button>
        }
      >
        <div className="form-stack">
          <p className={`callout ${result.enrolled ? 'callout-ok' : 'callout-warn'}`}>
            <Icon name={result.enrolled ? 'checkCircle' : 'alert'} size={16} />
            <span>{result.enrolled ? tn('sq.enrolled_n', result.enrolled) : t('sq.enrolled_none')}</span>
          </p>
          {result.enrolled && result.state !== 'active' ? <p className="muted small">{t('sq.not_started_yet')}</p> : null}
          {result.skipped.length ? (
            <section aria-labelledby="sq-left-out">
              <h3 id="sq-left-out" className="om-sub">
                {tn('om.left_out_n', result.skipped.length)}
              </h3>
              <ul className="om-list">
                {result.skipped.map((x) => (
                  <li key={x.contact}>
                    <span className="strong">{x.name || '—'}</span>
                    <span className="muted small">{x.reason === 'in_sequence' ? t('sq.r_in_sequence') : reasonText(x.reason, x.fields)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      title={t('sq.add_to')}
      onClose={onClose}
      size="md"
      footer={
        <Button variant="primary" icon="repeat" busy={busy} disabled={!pick} onClick={go}>
          {t('sq.add_people')}
        </Button>
      }
    >
      {!list ? (
        <Skeleton h={160} />
      ) : !list.length ? (
        <EmptyState icon="repeat" title={t('sq.none')} body={t('sq.none_hint_tab')} />
      ) : (
        <div className="form-stack">
          <p className="muted small">{companies.length ? tn('sq.for_companies', companies.length) : tn('sq.for_people', contacts.length)}</p>
          <Field label={t('sq.sequence')}>
            <select className="input" value={pick} onChange={(e) => setChosen(e.target.value)}>
              {list.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name} · {t(`sq.st_${x.state}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
          {q ? (
            <p className="muted small">
              {t('sq.total', { emails: tn('sq.n_emails', q.steps), days: tn('sq.n_days', q.days) })}
              {q.state !== 'active' ? ` · ${t('sq.not_started_yet')}` : ''}
            </p>
          ) : null}
          <p className="callout callout-info">
            <Icon name="shieldCheck" size={16} />
            <span>{t('sq.stops_when')}</span>
          </p>
        </div>
      )}
    </Dialog>
  );
}
